import type { ActionMap, FilterMap } from "@/extensions/hook-map";

const DEFAULT_PRIORITY = 10;

type FilterValue<K extends keyof FilterMap> = FilterMap[K] extends { value: infer V } ? V : never;
type FilterContext<K extends keyof FilterMap> = FilterMap[K] extends { context: infer C } ? C : never;

export type ActionCallback<K extends keyof ActionMap> = (payload: ActionMap[K]) => void;
export type FilterCallback<K extends keyof FilterMap> = (
  value: FilterValue<K>,
  context: FilterContext<K>,
) => FilterValue<K>;

interface Registration {
  namespace: string;
  priority: number;
  callback: (...args: any[]) => any;
}

const actions = new Map<string, Registration[]>();
const filters = new Map<string, Registration[]>();

const register = (
  store: Map<string, Registration[]>,
  hookName: string,
  entry: Registration,
) => {
  const list = (store.get(hookName) ?? []).filter((r) => r.namespace !== entry.namespace);
  list.push(entry);
  list.sort((a, b) => a.priority - b.priority);
  store.set(hookName, list);
};

const unregister = (store: Map<string, Registration[]>, hookName: string, namespace: string) => {
  const list = store.get(hookName);
  if (!list) return;
  store.set(
    hookName,
    list.filter((r) => r.namespace !== namespace),
  );
};

export function addAction<K extends keyof ActionMap>(
  hookName: K,
  namespace: string,
  callback: ActionCallback<K>,
  priority: number = DEFAULT_PRIORITY,
): () => void {
  register(actions, hookName, { namespace, priority, callback });
  return () => removeAction(hookName, namespace);
}

export function removeAction<K extends keyof ActionMap>(hookName: K, namespace: string) {
  unregister(actions, hookName, namespace);
}

export function doAction<K extends keyof ActionMap>(hookName: K, payload: ActionMap[K]) {
  for (const { namespace, callback } of [...(actions.get(hookName) ?? [])]) {
    try {
      callback(payload);
    } catch (error) {
      console.error(`[hooks] action "${hookName}" failed in "${namespace}"`, error);
    }
  }
}

// Filter callbacks must return a new value, never mutate the one passed in
export function addFilter<K extends keyof FilterMap>(
  hookName: K,
  namespace: string,
  callback: FilterCallback<K>,
  priority: number = DEFAULT_PRIORITY,
): () => void {
  register(filters, hookName as string, { namespace, priority, callback });
  return () => removeFilter(hookName, namespace);
}

export function removeFilter<K extends keyof FilterMap>(hookName: K, namespace: string) {
  unregister(filters, hookName as string, namespace);
}

export function applyFilters<K extends keyof FilterMap>(
  hookName: K,
  value: FilterValue<K>,
  context: FilterContext<K>,
): FilterValue<K> {
  let result = value;
  for (const { namespace, callback } of [...(filters.get(hookName as string) ?? [])]) {
    try {
      result = callback(result, context);
    } catch (error) {
      console.error(`[hooks] filter "${String(hookName)}" failed in "${namespace}"`, error);
    }
  }
  return result;
}
